import { ScaleElement } from '@scale/base-element';
import { CartAPI } from '@scale/cart-api';
import { fetchTextWithTimeout, getCartSections, parseSectionHTML } from '@scale/utilities';

const variantOf = (row) => row.querySelector('[data-variant-id]')?.dataset.variantId;

class ScaleCartUpsell extends ScaleElement {
  static observeRefs = true;

  #sectionUrl = null;
  #loadController;
  #sight;

  setup() {
    this.on('click', (event) => {
      const button = event.target.closest('[data-variant-id]');
      if (button) this.#addToCart(button);
    });

    if (this.dataset.sectionUrl) this.#loadWhenSeen();
  }

  updatedCallback() {
    const url = this.dataset.sectionUrl;
    if (url && (url !== this.#sectionUrl || !this.ref('grid')?.children.length)) this.#loadWhenSeen();
  }

  teardown() {
    this.#loadController?.abort();
    this.#sight?.disconnect();
  }

  #loadWhenSeen() {
    this.#sight?.disconnect();
    this.hidden = false;
    this.#sight = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.#sight.disconnect();
      this.#load();
    }, { rootMargin: '200px' });
    this.#sight.observe(this);
  }

  async #load() {
    const url = this.dataset.sectionUrl;
    this.#sectionUrl = url;
    this.#loadController?.abort();
    const controller = new AbortController();
    this.#loadController = controller;
    this.hidden = false;
    this.setAttribute('loading', '');
    this.setAttribute('aria-busy', 'true');

    try {
      const limit = this.getNumber('limit', 4);
      const rows = await this.#fetchRows(url, controller.signal);
      const fallback = this.dataset.fallbackUrl;
      if (rows.length < limit && fallback) {
        const shown = new Set(rows.map(variantOf));
        rows.push(...(await this.#fetchRows(fallback, controller.signal)).filter((row) => !shown.has(variantOf(row))));
      }
      if (this.#loadController !== controller) return;
      this.#render(rows.slice(0, limit).map((row) => row.outerHTML));
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (this.#loadController === controller) this.#settleHidden();
    } finally {
      if (this.#loadController === controller) this.#loadController = null;
    }
  }

  async #fetchRows(url, signal) {
    const { response, text } = await fetchTextWithTimeout(url, { signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return [...parseSectionHTML(text).querySelectorAll('.cart-upsell-item')];
  }

  #render(items) {
    const grid = this.ref('grid');
    if (!grid || !items.length) {
      this.#settleHidden();
      return;
    }

    grid.innerHTML = items.join('');
    this.removeAttribute('loading');
    this.removeAttribute('aria-busy');
  }

  #settleHidden() {
    if (!this.hasAttribute('data-visual-preview-mode')) this.hidden = true;
    this.removeAttribute('loading');
    this.removeAttribute('aria-busy');
  }

  async #addToCart(button) {
    const variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) return;

    button.disabled = true;
    try {
      await CartAPI.add([{ id: Number(variantId), quantity: 1 }], {
        sections: getCartSections(),
        target: this,
        context: this.closest('scale-cart-drawer') ? 'dialog' : 'cart',
      });
    } catch {
      button.disabled = false;
    }
  }
}

if (!customElements.get('scale-cart-upsell')) {
  customElements.define('scale-cart-upsell', ScaleCartUpsell);
}
